/**
 * Pre-release analytics consent gate.
 *
 * Shown once on first launch (or after data clear). On agree, persists a
 * timestamp in app_meta so the modal never re-shows. On exit, redirects away
 * from the app — declining users do not load gtag.js.
 *
 * TEST BUILD ONLY — delete this file along with src/analytics/ at launch.
 */

import { metaGet, metaSet } from '../storage/db.js';
import { nowIso } from '../core/time.js';

const EXIT_URL = 'https://www.google.com/';

/** Returns true if the user already consented previously. */
export async function hasAnalyticsConsent(): Promise<boolean> {
  const at = await metaGet('analytics_consent_at');
  return typeof at === 'string' && at.length > 0;
}

/** Persist consent. Idempotent — overwrites with latest timestamp. */
export async function recordConsent(): Promise<void> {
  await metaSet('analytics_consent_at', nowIso());
}

/**
 * Render a blocking modal. Resolves true on agree, false on exit.
 * The modal is appended to <body> directly so it works pre-app-render.
 * Caller is responsible for redirecting on false.
 */
export function showConsentModal(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'wean-consent-title');
    overlay.setAttribute('style', [
      'position:fixed', 'inset:0',
      'background:rgba(11,13,19,0.92)',
      'backdrop-filter:blur(6px)', '-webkit-backdrop-filter:blur(6px)',
      'display:flex', 'align-items:center', 'justify-content:center',
      'padding:1.25rem', 'z-index:2147483647',
      'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
      'color:#eef1f7',
    ].join(';'));

    const panel = document.createElement('div');
    panel.setAttribute('style', [
      'max-width:480px', 'width:100%',
      'background:#161a23', 'border:1px solid rgba(255,255,255,0.08)',
      'border-radius:14px', 'padding:1.5rem',
      'box-shadow:0 12px 40px rgba(0,0,0,0.5)',
      'line-height:1.5',
    ].join(';'));

    const title = document.createElement('h2');
    title.id = 'wean-consent-title';
    title.textContent = 'Pre-release tester notice';
    title.setAttribute('style', 'margin:0 0 0.75rem;font-size:1.15rem;color:#5eead4;');

    const body = document.createElement('p');
    body.textContent =
      'Wean is currently an internal pre-release. Google Analytics is enabled ' +
      'and anonymously tracks usage data to help diagnose errors and measure ' +
      'engagement. No personal information, free-text content, or habit details ' +
      'are sent. When Wean launches publicly, this tracking will be removed ' +
      'completely.';
    body.setAttribute('style', 'margin:0 0 1rem;font-size:0.95rem;color:#cdd3e0;');

    const sub = document.createElement('p');
    sub.textContent = 'You can clear local data in Settings to revoke this choice.';
    sub.setAttribute('style', 'margin:0 0 1.25rem;font-size:0.8rem;color:#8089a0;');

    const buttons = document.createElement('div');
    buttons.setAttribute('style', 'display:flex;gap:0.5rem;flex-wrap:wrap;justify-content:flex-end;');

    const exitBtn = document.createElement('button');
    exitBtn.type = 'button';
    exitBtn.textContent = 'Exit';
    exitBtn.setAttribute('style', [
      'flex:0 0 auto', 'padding:0.65rem 1.1rem',
      'background:transparent', 'color:#cdd3e0',
      'border:1px solid rgba(255,255,255,0.18)', 'border-radius:10px',
      'font-size:0.95rem', 'cursor:pointer',
    ].join(';'));

    const agreeBtn = document.createElement('button');
    agreeBtn.type = 'button';
    agreeBtn.textContent = 'I agree';
    agreeBtn.setAttribute('style', [
      'flex:0 0 auto', 'padding:0.65rem 1.25rem',
      'background:linear-gradient(135deg,#5eead4,#67e8f9)',
      'color:#0b0d13', 'border:none', 'border-radius:10px',
      'font-size:0.95rem', 'font-weight:600', 'cursor:pointer',
    ].join(';'));

    const cleanup = () => {
      overlay.remove();
    };

    exitBtn.addEventListener('click', () => {
      cleanup();
      resolve(false);
    });
    agreeBtn.addEventListener('click', () => {
      cleanup();
      resolve(true);
    });

    buttons.appendChild(exitBtn);
    buttons.appendChild(agreeBtn);
    panel.appendChild(title);
    panel.appendChild(body);
    panel.appendChild(sub);
    panel.appendChild(buttons);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Move focus to the primary action so keyboard users can confirm/exit
    // immediately. Falls back silently if focus is denied (e.g. headless).
    try { agreeBtn.focus(); } catch { /* ignore */ }
  });
}

/**
 * Show the modal if no prior consent. Redirects on exit; records consent on
 * agree. Returns true when the app should continue booting.
 */
export async function ensureAnalyticsConsent(): Promise<boolean> {
  if (await hasAnalyticsConsent()) return true;
  const agreed = await showConsentModal();
  if (!agreed) {
    location.replace(EXIT_URL);
    return false;
  }
  await recordConsent();
  return true;
}
